import { supabase } from '@loop3/supabase-client'
import {
  companyBySlug,
  companyDetail,
  escapeIlike,
  listClientJournal,
  listClientTeam,
  personDetail,
  searchDirectory,
  type DirectoryKind,
} from '@loop3/universe'
import type {
  CompanyDetail,
  Entity,
  EntityKind,
  PersonDetail,
} from '@/lib/types/universe'

/** Rows per kind when Explorer shows several kinds at once (one screen of cards). */
export const BROWSE_LIMIT = 12
/** Rows when Explorer is filtered to a single kind, so a whole roster fits. */
export const KIND_LIMIT = 200

/** Searches clients, team members, companies and persons by name or email; an empty query browses. */
export async function searchUniverse(
  rawQuery: string,
  kinds: EntityKind[]
): Promise<Entity[]> {
  const directory = kinds.filter(
    (k): k is DirectoryKind => k !== 'solution' && k !== 'meeting'
  )
  const rows = await searchDirectory(
    supabase,
    escapeIlike(rawQuery),
    directory,
    directory.length === 1 ? KIND_LIMIT : BROWSE_LIMIT
  )
  return [
    ...rows.clients.map(
      (c): Entity => ({
        kind: 'client',
        id: c.prefix,
        title: c.name,
        subtitle: c.prefix,
      })
    ),
    ...rows.team_members.map((p): Entity => {
      const e: Entity = {
        kind: 'team_member',
        title: p.name,
        subtitle: p.role ?? 'Loop3',
      }
      if (p.person_id) e.id = p.person_id
      return e
    }),
    ...rows.companies.map(
      (c): Entity => ({
        kind: 'company',
        id: c.slug,
        title: c.name,
        subtitle: c.kind ?? 'Company',
      })
    ),
    ...rows.persons.map(
      (p): Entity => ({
        kind: 'person',
        id: p.id,
        title: p.full_name,
        subtitle: p.email ?? undefined,
      })
    ),
  ]
}

/** One company by exact slug with open relationships, current people and linked non-archived client. */
export async function getCompany(slug: string): Promise<CompanyDetail | null> {
  const c = await companyBySlug(supabase, slug)
  return c ? companyDetail(supabase, c) : null
}

/** One person with affiliations, client-contact roles, team row, enrichment profile and priority. */
export function getPerson(id: string): Promise<PersonDetail | null> {
  return personDetail(supabase, id)
}

/** Planned team spans for one client (no hours). */
export function fetchClientTeam(clientId: string) {
  return listClientTeam(supabase, clientId)
}

/** A client's journal weeks between two dates (YYYY-MM-DD). */
export function fetchClientJournal(
  clientId: string,
  window: { from: string; to: string }
) {
  return listClientJournal(supabase, clientId, window)
}
